import { useState, useEffect, useCallback } from 'react';
import { ABTestingService, ABVariant, ABExperiment } from '@/services/ab-testing/abTestingService';

// Generate or get session ID for A/B testing
const getABSessionId = (): string => {
  let sessionId = sessionStorage.getItem('ab_testing_session_id');
  if (!sessionId) {
    sessionId = `ab_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('ab_testing_session_id', sessionId);
  }
  return sessionId;
};

export const useABTesting = () => {
  const [experiments, setExperiments] = useState<ABExperiment[]>([]);
  const [assignments, setAssignments] = useState<Record<string, ABVariant>>({});
  const [loading, setLoading] = useState(true);
  const sessionId = getABSessionId();

  // Load active experiments and assignments
  useEffect(() => {
    loadExperiments();
  }, []);

  const loadExperiments = async () => {
    try {
      setLoading(true);
      const activeExperiments = await ABTestingService.getActiveExperiments();
      setExperiments(activeExperiments);

      // Get assignments for each experiment
      const assignmentPromises = activeExperiments.map(async (experiment) => {
        const assignment = await ABTestingService.getUserAssignment(experiment.id, sessionId);
        return { experimentId: experiment.id, assignment };
      });

      const assignmentResults = await Promise.all(assignmentPromises);
      const newAssignments: Record<string, ABVariant> = {};
      
      assignmentResults.forEach(({ experimentId, assignment }) => {
        if (assignment) {
          newAssignments[experimentId] = assignment;
        }
      });

      setAssignments(newAssignments);
    } catch (error) {
      console.error('Error loading experiments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get variant for a specific experiment
  const getVariant = useCallback(async (experimentName: string): Promise<ABVariant | null> => {
    const experiment = experiments.find(exp => exp.name === experimentName);
    if (!experiment) return null;

    // Return existing assignment if available
    if (assignments[experiment.id]) {
      return assignments[experiment.id];
    }

    // Assign to variant if not already assigned
    const variant = await ABTestingService.assignToVariant(experiment.id, sessionId);
    if (variant) {
      setAssignments(prev => ({
        ...prev,
        [experiment.id]: variant
      }));
    }

    return variant;
  }, [experiments, assignments, sessionId]);

  // Check if user is in a specific variant
  const isInVariant = useCallback(async (experimentName: string, variantName: string): Promise<boolean> => {
    const variant = await getVariant(experimentName);
    return variant?.name === variantName;
  }, [getVariant]);

  // Get configuration for a variant
  const getVariantConfig = useCallback(async (experimentName: string): Promise<any> => {
    const variant = await getVariant(experimentName);
    return variant?.config || {};
  }, [getVariant]);

  // Record conversion event
  const recordConversion = useCallback(async (
    experimentName: string,
    eventType: string,
    eventValue?: number,
    metadata?: any
  ): Promise<boolean> => {
    const experiment = experiments.find(exp => exp.name === experimentName);
    if (!experiment) return false;

    return ABTestingService.recordConversion(
      experiment.id,
      sessionId,
      eventType,
      eventValue,
      metadata
    );
  }, [experiments, sessionId]);

  return {
    experiments,
    assignments,
    loading,
    getVariant,
    isInVariant,
    getVariantConfig,
    recordConversion,
    refreshExperiments: loadExperiments
  };
};

// Hook for specific A/B test
export const useABTest = (experimentName: string) => {
  const { getVariant, isInVariant, getVariantConfig, recordConversion } = useABTesting();
  const [variant, setVariant] = useState<ABVariant | null>(null);
  const [config, setConfig] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVariant();
  }, [experimentName]);

  const loadVariant = async () => {
    try {
      setLoading(true);
      const [variantData, configData] = await Promise.all([
        getVariant(experimentName),
        getVariantConfig(experimentName)
      ]);
      
      setVariant(variantData);
      setConfig(configData);
    } catch (error) {
      console.error('Error loading variant:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkVariant = useCallback(async (variantName: string): Promise<boolean> => {
    return isInVariant(experimentName, variantName);
  }, [experimentName, isInVariant]);

  const convert = useCallback(async (
    eventType: string,
    eventValue?: number,
    metadata?: any
  ): Promise<boolean> => {
    return recordConversion(experimentName, eventType, eventValue, metadata);
  }, [experimentName, recordConversion]);

  return {
    variant,
    config,
    loading,
    isInVariant: checkVariant,
    recordConversion: convert
  };
};